import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SF_COLORS } from '../../../../styles/brandColors';
import './BouncingCircles.css';

const SPEED = 0.5;
const BOTTOM_PAD = 120;

function getRadius() {
  const w = window.innerWidth;
  if (w < 480) return 30;
  if (w < 768) return 40;
  return 60;
}

function randomVelocity() {
  const angle = Math.random() * 2 * Math.PI;
  return { vx: Math.cos(angle) * SPEED, vy: Math.sin(angle) * SPEED };
}

function initCircles(brands, boxWidth, boxHeight, radius) {
  const circles = [];
  for (let i = 0; i < brands.length; i++) {
    let x, y, overlap;
    let attempts = 0;
    do {
      overlap = false;
      x = radius + Math.random() * (boxWidth - radius * 2);
      y = radius + Math.random() * (boxHeight - radius * 2);
      for (const c of circles) {
        const dx = c.x - x;
        const dy = c.y - y;
        if (Math.sqrt(dx * dx + dy * dy) < radius * 2.2) {
          overlap = true;
          break;
        }
      }
      attempts++;
    } while (overlap && attempts < 300);
    const { vx, vy } = randomVelocity();
    circles.push({
      id: i,
      x,
      y,
      vx,
      vy,
      scale: 1,
      opacity: 0.3,
      name: brands[i].nombre,
      marcaId: brands[i].leora_marcas_UUID,
      logo: brands[i].logo,
    });
  }
  return circles;
}

function BouncingCircles({ brands = [] }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const circlesRef = useRef(null);
  const hoveredRef = useRef(null);
  const animRef = useRef(null);
  const radiusRef = useRef(getRadius());
  const sizeRef = useRef({ w: window.innerWidth, h: window.innerHeight });
  const [, setRenderTick] = useState(0);
  const [dimensions, setDimensions] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [reduceMotion, setReduceMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Initialize / reinitialize when brands change
  useEffect(() => {
    if (brands.length === 0) return;
    const w = window.innerWidth;
    const r = getRadius();
    radiusRef.current = r;
    const h = window.innerHeight - BOTTOM_PAD;
    sizeRef.current = { w, h };
    setDimensions({ w: window.innerWidth, h: window.innerHeight });
    circlesRef.current = initCircles(brands, w, h, r);
  }, [brands]);

  // Responsive resize
  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      const r = getRadius();
      radiusRef.current = r;
      const h = window.innerHeight - BOTTOM_PAD;
      sizeRef.current = { w, h };
      setDimensions({ w: window.innerWidth, h: window.innerHeight });
      if (circlesRef.current) {
        for (const c of circlesRef.current) {
          if (c.x + r > w) c.x = w - r;
          if (c.x - r < 0) c.x = r;
          if (c.y + r > h) c.y = h - r;
          if (c.y - r < 0) c.y = r;
        }
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const tick = useCallback(() => {
    const circles = circlesRef.current;
    if (!circles) { animRef.current = requestAnimationFrame(tick); return; }
    const { w: BOX_W, h: BOX_H } = sizeRef.current;
    const R = radiusRef.current;

    for (const c of circles) {
      if (hoveredRef.current === c.id) continue;
      c.x += c.vx;
      c.y += c.vy;

      if (c.x - R < 0) { c.x = R; c.vx = Math.abs(c.vx); }
      if (c.x + R > BOX_W) { c.x = BOX_W - R; c.vx = -Math.abs(c.vx); }
      if (c.y - R < 0) { c.y = R; c.vy = Math.abs(c.vy); }
      if (c.y + R > BOX_H) { c.y = BOX_H - R; c.vy = -Math.abs(c.vy); }
    }

    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        const a = circles[i];
        const b = circles[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = R * 2;

        if (dist < minDist && dist > 0) {
          const overlap = (minDist - dist) / 2;
          const nx = dx / dist;
          const ny = dy / dist;

          if (hoveredRef.current !== a.id) { a.x -= nx * overlap; a.y -= ny * overlap; }
          if (hoveredRef.current !== b.id) { b.x += nx * overlap; b.y += ny * overlap; }

          const relVx = a.vx - b.vx;
          const relVy = a.vy - b.vy;
          const dot = relVx * nx + relVy * ny;

          if (dot > 0) {
            if (hoveredRef.current !== a.id) { a.vx -= dot * nx; a.vy -= dot * ny; }
            if (hoveredRef.current !== b.id) { b.vx += dot * nx; b.vy += dot * ny; }
          }

          const normA = Math.sqrt(a.vx * a.vx + a.vy * a.vy);
          const normB = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
          if (normA > 0 && hoveredRef.current !== a.id) { a.vx = (a.vx / normA) * SPEED; a.vy = (a.vy / normA) * SPEED; }
          if (normB > 0 && hoveredRef.current !== b.id) { b.vx = (b.vx / normB) * SPEED; b.vy = (b.vy / normB) * SPEED; }
        }
      }
    }

    setRenderTick(t => t + 1);

    // Smooth scale & opacity interpolation
    for (const c of circles) {
      const isHov = hoveredRef.current === c.id;
      const targetScale = isHov ? 1.4 : 1;
      const targetOpacity = isHov ? 1 : 0.3;
      c.scale += (targetScale - c.scale) * 0.12;
      c.opacity += (targetOpacity - c.opacity) * 0.12;
    }

    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      return undefined;
    }
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [tick, reduceMotion]);

  if (brands.length === 0) return null;

  const R = radiusRef.current;

  return (
    <div className="bouncing-circles-fullscreen" ref={containerRef}>
      <svg
        width={dimensions.w}
        height={dimensions.h}
        className="bouncing-circles-svg"
        viewBox={`0 0 ${dimensions.w} ${dimensions.h}`}
      >
        <defs>
          {(circlesRef.current || []).map((c) => {
            const size = R * 2 * c.scale;
            return (
              <clipPath key={`clip-${c.id}`} id={`clip-${c.id}`}>
                <rect
                  x={c.x - size / 2}
                  y={c.y - size / 2}
                  width={size}
                  height={size}
                  rx={12}
                  ry={12}
                />
              </clipPath>
            );
          })}
        </defs>
        {(circlesRef.current || []).map((c, index) => {
          const size = R * 2 * c.scale;
          const isHovered = hoveredRef.current === c.id;
          return (
            <g
              key={c.id}
              className="bouncing-circle-enter"
              style={{ '--enter-delay': `${index * 45}ms` }}
            >
              <g
                onMouseEnter={() => { hoveredRef.current = c.id; }}
                onMouseLeave={() => { hoveredRef.current = null; }}
                onClick={() => { navigate(`/productos?marca=${c.marcaId}`); }}
                style={{
                  cursor: 'pointer',
                  opacity: c.opacity,
                  filter: isHovered ? `drop-shadow(0 0 ${24 * (c.scale - 1) / 0.4}px ${SF_COLORS.accentGlow})` : 'none',
                }}
              >
                <image
                  href={c.logo}
                  x={c.x - size / 2}
                  y={c.y - size / 2}
                  width={size}
                  height={size}
                  clipPath={`url(#clip-${c.id})`}
                  preserveAspectRatio="xMidYMid meet"
                />
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default BouncingCircles;
